import React, { useEffect } from 'react';
import AnimatedBackground from '../components/AnimatedBackground';
import { GYM_INFO, PERSONAL_TRAINING, TRAINING_IMAGES } from '../mock';
import { User, Video, Trophy, Smartphone, ArrowRight } from 'lucide-react';

const PersonalTraining = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const trainingTypes = [
    {
      icon: User,
      title: 'In-Gym 1-on-1',
      description: 'Dedicated trainer, personalized attention, maximum results',
    },
    {
      icon: Video,
      title: 'Online Coaching',
      description: 'Train from anywhere with expert remote guidance',
    },
    {
      icon: Trophy,
      title: 'Competition Prep',
      description: 'Elite coaching for bodybuilding & powerlifting competitions',
    },
    {
      icon: Smartphone,
      title: 'Beginner Foundation',
      description: 'Perfect starting point for fitness newcomers',
    },
  ];

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <AnimatedBackground />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={TRAINING_IMAGES.personalTraining}
            alt="Personal Training"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        </div>

        <div className="relative z-10 container mx-auto px-6">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 uppercase leading-tight">
              Personal <span className="text-[#39FF14]">Training</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Get expert 1-on-1 guidance from certified trainers. Customized programs designed specifically for your goals, fitness level, and lifestyle.
            </p>
            <a
              href={`${GYM_INFO.whatsappLink}?text=Hi, I'm interested in Personal Training at Iron Crust Gym`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#39FF14] text-black font-bold text-lg uppercase rounded-full hover:scale-105 hover:shadow-2xl hover:shadow-[#39FF14]/50 transition-all duration-300"
            >
              Book Free Consultation
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6">
        {/* Training Types */}
        <section className="py-16">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-12 uppercase">
            Training <span className="text-[#39FF14]">Options</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-16">
            {trainingTypes.map((type, idx) => (
              <div
                key={idx}
                className="bg-gradient-to-br from-[#1a1a1a] to-black p-6 rounded-2xl border border-[#39FF14]/20 hover:border-[#39FF14] transition-all duration-300 hover:scale-105"
              >
                <div className="w-14 h-14 bg-[#39FF14]/20 rounded-xl flex items-center justify-center mb-4">
                  <type.icon className="w-7 h-7 text-[#39FF14]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 uppercase">{type.title}</h3>
                <p className="text-gray-400 text-sm">{type.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Programs */}
        <section className="py-16">
          <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-12 uppercase">
            Our <span className="text-[#39FF14]">Programs</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {PERSONAL_TRAINING.map((program) => (
              <div
                key={program.id}
                className="bg-gradient-to-br from-[#1a1a1a] to-black p-8 rounded-3xl border border-[#39FF14]/20 hover:border-[#39FF14] transition-all duration-300 hover:scale-105"
              >
                <div className="mb-6">
                  <h3 className="text-2xl font-black text-white mb-2 uppercase">{program.name}</h3>
                  <p className="text-[#39FF14] font-bold">{program.duration}</p>
                </div>

                <p className="text-gray-400 mb-6">{program.description}</p>

                <ul className="space-y-3 mb-8">
                  {program.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                      <span className="w-1.5 h-1.5 bg-[#39FF14] rounded-full mt-1.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="pt-6 border-t border-[#39FF14]/20">
                  <div className="text-4xl font-black text-[#39FF14] mb-4">₹{program.price}</div>
                  <a
                    href={`${GYM_INFO.whatsappLink}?text=Hi, I'm interested in ${program.name} (₹${program.price})`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full px-6 py-3 bg-[#39FF14] text-black font-bold text-center uppercase rounded-full hover:scale-105 transition-transform"
                  >
                    Get Started
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto bg-gradient-to-br from-[#1a1a1a] to-black p-12 rounded-3xl border border-[#39FF14]/20">
            <h2 className="text-4xl md:text-5xl font-black text-white text-center mb-12 uppercase">
              Why Personal <span className="text-[#39FF14]">Training</span>?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Customized workout plans for your specific goals',
                'Expert form correction to prevent injuries',
                'Faster results with accountability & motivation',
                'Personalized nutrition guidance included',
                'Flexible scheduling around your lifestyle',
                'Regular progress tracking & adjustments',
              ].map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#39FF14]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <div className="w-2 h-2 bg-[#39FF14] rounded-full" />
                  </div>
                  <span className="text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 text-center">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 uppercase">
            Ready to <span className="text-[#39FF14]">Transform</span>?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Book a free consultation with one of our expert trainers today
          </p>
          <a
            href={`${GYM_INFO.whatsappLink}?text=Hi, I want to book a free consultation for Personal Training`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#39FF14] text-black font-bold text-lg uppercase rounded-full hover:scale-105 hover:shadow-2xl hover:shadow-[#39FF14]/50 transition-all duration-300"
          >
            Book Free Consultation
            <ArrowRight className="w-5 h-5" />
          </a>
        </section>
      </div>
    </div>
  );
};

export default PersonalTraining;
